import { useErrorHandler } from 'react-error-boundary'
import { useScroll, useScrolling } from 'react-use'
import React, { useEffect, useState, useMemo } from 'react'
import { useInView, InView } from 'react-intersection-observer'
import debounce from 'lodash/debounce'
import clsx from 'clsx'

import ListItem from 'app/InspirationPage/components/revamp/ListItem'
import { useAPIGetInspirationList } from 'api/inspirations/useAPIInspirations'
import ListItemLoader from 'app/InspirationPage/components/revamp/ListItemLoader'
import { useInspirationsStore } from 'stores/inspirations'
import { useSETrackEvent } from 'app/InspirationPage/hooks/useSETrackEvent'

import type { FC } from 'react'

interface BuktiKaryaBlockProps {
  categorySlug?: string
  isDesktop?: boolean
}

const BuktiKaryaBlock: FC<BuktiKaryaBlockProps> = ({
  categorySlug,
  isDesktop = false,
}) => {
  const scrollRef = React.useRef(null)
  const scrolling = useScrolling(scrollRef)
  const { y: scrollTopY } = useScroll(scrollRef)
  const {
    // States
    inspirationListScroll,

    // Mutations
    setInspirationListScroll,
  } = useInspirationsStore()
  const [loadingRef, loadingIsInView] = useInView()
  const handleError = useErrorHandler()

  const {
    SETrackBuktiKaryaCategoryClickIdePraktik,
    SETrackBuktiKaryaCategoryScrollIdePraktik,
    SETrackBuktiKaryaExploreClickIdePraktik,
    SETrackBuktiKaryaExploreScrollIdePraktik,
  } = useSETrackEvent()

  const { fetchNextPage, ...inspirationList } = useAPIGetInspirationList({
    categorySlug,
    contentSources: ['CS_BUKTI_KARYA'],
  })

  if (inspirationList.isError) {
    handleError(inspirationList.error)
  }

  React.useEffect(() => {
    if (loadingIsInView) {
      fetchNextPage()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingIsInView])

  // Auto-fetch next page when desktop grid makes page non-scrollable (Pattern G)
  const hasData = (inspirationList?.data?.pages?.[0]?.data?.length ?? 0) > 0
  useEffect(() => {
    if (
      !isDesktop ||
      !inspirationList.isSuccess ||
      inspirationList.isLoading ||
      !hasData ||
      !inspirationList.hasNextPage ||
      inspirationList.isFetchingNextPage
    ) {
      return
    }

    const isPageScrollable =
      document.documentElement.scrollHeight > window.innerHeight

    if (!isPageScrollable) {
      fetchNextPage()
    }
  }, [
    inspirationList?.data?.pages?.length,
    fetchNextPage,
    inspirationList.hasNextPage,
    isDesktop,
    inspirationList.isFetchingNextPage,
    inspirationList.isLoading,
    inspirationList.isSuccess,
    hasData,
  ])

  React.useEffect(() => {
    const currentScroll = inspirationListScroll ?? scrollTopY
    scrollRef?.current?.scrollTo?.(0, currentScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (!scrolling && scrollTopY > 0) {
      setInspirationListScroll(scrollTopY)
    }
    // eslint-disable-next-line
  }, [scrolling])

  const getPosition = (offset: number, index: number) => offset + index + 1

  const handleClickItemTracker = (contentId: string, contentPage: number) => {
    if (!!categorySlug) {
      SETrackBuktiKaryaCategoryClickIdePraktik({
        contentId,
        contentPage,
        pageLocation: categorySlug,
      })
    } else {
      SETrackBuktiKaryaExploreClickIdePraktik({ contentId, contentPage })
    }
  }

  /**
   * SCROLL TRACKER
   */
  const [inViewItems, setInViewItems] = useState<
    Array<{
      contentId: string
      contentPage: number
    }>
  >([])

  // This effect will be called in every `inView` events
  useEffect(() => {
    if (inViewItems.length > 0) {
      handleInViewItemTracker(inViewItems)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inViewItems])

  // We need to debounce (1 second) the tracker to avoid it being called in every `inView` events.
  const handleInViewItemTracker = useMemo(
    () =>
      debounce((items) => {
        const contentIds = items.map((item) => item.contentId)
        const contentPages = items.map((item) => item.contentPage)

        if (!!categorySlug) {
          SETrackBuktiKaryaCategoryScrollIdePraktik({
            contentIds,
            contentPages,
            pageLocation: categorySlug,
          })
        } else {
          SETrackBuktiKaryaExploreScrollIdePraktik({
            contentIds,
            contentPages,
          })
        }

        // Remove the previously sent items
        setInViewItems([])
      }, 1000),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  // END SCROLL TRACKER

  return (
    <div
      className={clsx(
        isDesktop ? 'grid grid-cols-2 lg:grid-cols-3 gap-4 py-4' : 'space-y-2'
      )}
    >
      {inspirationList.isLoading && (
        <ListItemLoader count={isDesktop ? 6 : 3} />
      )}

      {(inspirationList?.data?.pages ?? []).map((page, index) => {
        return (
          <React.Fragment key={index}>
            {page.data.map((item, itemIndex) => {
              const position = getPosition(page.meta.offset, itemIndex)
              const id = `list-item-${position}`

              return (
                <InView
                  as="div"
                  key={id}
                  data-testid={id}
                  threshold={1}
                  triggerOnce
                  onChange={(inView) => {
                    if (inView) {
                      setInViewItems((prevItems) =>
                        prevItems.concat({
                          contentId: item.contentId,
                          contentPage: position,
                        })
                      )
                    }
                  }}
                >
                  <ListItem
                    data-testid="inspiration-list-item"
                    containerClassName={
                      isDesktop
                        ? 'bg-white border border-subdued rounded-lg hover:shadow-md transition-shadow'
                        : 'bg-white border border-subdued rounded'
                    }
                    data={item}
                    isDesktop={isDesktop}
                    onClick={() => {
                      handleClickItemTracker(item.contentId, position)
                    }}
                  />
                </InView>
              )
            })}
          </React.Fragment>
        )
      })}

      {inspirationList.hasNextPage && (
        <div ref={loadingRef}>
          <ListItemLoader />
        </div>
      )}
    </div>
  )
}
export default BuktiKaryaBlock
